// src/pages/referrals/ReferralDashboard.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  useToast,
  VStack,
  HStack,
  Spinner,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import axiosInstance from '../../services/axios';
import { useAuth } from '../../hooks/useAuth';

const ReferralDashboard = () => {
  const toast = useToast();
  const { user } = useAuth();
  const [referral, setReferral] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch the current user's referral record if they have one
  const fetchReferral = useCallback(async () => {
    if (!user) {
      toast({
        title: 'Please login to view your referrals',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!user.referral_id) {
      toast({
        title: 'Referral code not found. Please contact support.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setIsLoading(true);
      // Use an endpoint that returns the referral record for the current user
      const response = await axiosInstance.get('/referrals/my');
      setReferral(response.data);
    } catch (error) {
      toast({
        title: 'Error loading referral data',
        description:
          error.response?.data?.detail || error.message || 'Unknown error',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  // Function to copy the referral code to clipboard
  const handleCopy = async () => {
    if (referral && referral.referral_code) {
      try {
        const referralLink = `${window.location.origin}/register?ref=${referral.referral_code}`;
        await navigator.clipboard.writeText(referralLink);
        toast({
          title: 'Referral link copied!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (err) {
        toast({
          title: 'Failed to copy referral link',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      }
    }
  };

  // Function to share the referral link using the native share API, if available
  const handleShare = async () => {
    if (referral && referral.referral_code) {
      const referralLink = `${window.location.origin}/register?ref=${referral.referral_code}`;
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join using my referral code!',
            text: `Use my referral code: ${referral.referral_code}`,
            url: referralLink,
          });
          toast({
            title: 'Referral link shared!',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        } catch (err) {
          toast({
            title: 'Error sharing referral link, link copied instead',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
          handleCopy();
        }
      } else {
        // Fallback: copy the referral link if native share is not supported
        handleCopy();
      }
    }
  };

  useEffect(() => {
    if (user && user.referral_id) {
      fetchReferral();
    }
  }, [user, fetchReferral]);

  return (
    <Box p={6}>
      <Heading mb={6}>Your Referral Information</Heading>

      {isLoading && (
        <HStack justifyContent="center">
          <Spinner size="lg" />
        </HStack>
      )}

      {!isLoading && !referral && (
        <Text color="primary.900">
          Please login to view your referral information.
        </Text>
      )}

      {!isLoading && referral && (
        <VStack spacing={4} align="stretch">
          <Box p={4} borderWidth="1px" borderRadius="md" bg="secondary.100">
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight="bold">
                Referral Code: <Text as="span" color="green.500">{referral.referral_code}</Text>
              </Text>
              <HStack spacing={2}>
                <IconButton
                  onClick={handleCopy}
                  icon={<CopyIcon />}
                  aria-label="Copy referral link"
                  variant="brand"
                  size="sm"
                  _hover={{ color: 'dark.900' }}
                />
                <IconButton
                  onClick={handleShare}
                  icon={<ExternalLinkIcon />}
                  aria-label="Share referral link"
                  variant="brand"
                  size="sm"
                  _hover={{ color: 'dark.900' }}
                />
              </HStack>
            </Flex>
            <Text fontSize="sm" mt={2}>
              Users referred: {referral.referred_user_ids ? referral.referred_user_ids.length : 0}
            </Text>
            <Text fontSize="sm" mt={2}>
              Referral Balance: ${referral.referral_balance.toFixed(2)}
            </Text>

          </Box>
        </VStack>
      )}
    </Box>
  );
};

export default ReferralDashboard;